// The package schema declares draft 2020-12, so the matching Ajv build is
// required — the default export only knows draft-07 and cannot resolve it.
import Ajv2020, { type ValidateFunction } from 'ajv/dist/2020';
import type {
  AIComposerContext,
  AIInterfaceProposal,
  LayoutPatchOperation,
} from '@shared/contracts/customisation';
import schema from '@shared/schemas/ai-layout-patch.schema.json';
import {
  MOBILE_WIDGETS,
  widgetById,
  type MobileWidgetId,
} from '@/customisation/widgetRegistry';
import { POLICY_VERSION, entitledWidgetIds, validateOperations } from '@/customisation/policyEngine';
import type { EntitlementContext } from '@/services/entitlements';

/**
 * AI compact-layout composer.
 *
 * Implements the mandated pipeline exactly:
 *
 *   goal → clarification → entitled-widget retrieval → typed LayoutPatch
 *        → deterministic policy validation → preview + diff + rationale
 *        → explicit human Apply → version + audit → undo / rollback
 *
 * Hard safety properties, each enforced structurally rather than by prompt:
 *
 *  - The proposal is emitted as JSON and validated against the package's own
 *    `ai-layout-patch.schema.json`. Anything non-conforming is rejected.
 *  - Operations may only reference widget IDs already in the registry AND
 *    already entitled to this user. The retrieval step is the only source of
 *    candidate IDs.
 *  - `policyResult` from the generator is discarded and recomputed by the
 *    deterministic engine.
 *  - The user's text is untrusted input. It is scanned for injection attempts,
 *    never interpreted as instructions to the surrounding system, and never
 *    used to widen entitlement.
 *  - No code path produces JSX, HTML, scripts or dynamic imports. The output
 *    type is a fixed list of five operation verbs over registered widgets.
 */

const ajv = new Ajv2020({ allErrors: true, strict: false });
let validator: ValidateFunction | null = null;

function schemaValidator(): ValidateFunction {
  validator ??= ajv.compile(schema);
  return validator;
}

let proposalSeq = 0;

/* ------------------------------------------------------- untrusted input -- */

/**
 * Patterns that indicate an attempt to talk past the composer into the
 * surrounding system. Matching text does not abort the request — it is simply
 * never treated as an instruction, and the attempt is surfaced to the user.
 */
const INJECTION_PATTERNS: readonly RegExp[] = [
  /ignore (all |any )?(previous|prior|above) instructions?/i,
  /disregard (the )?(rules|policy|system)/i,
  /you are now|act as (a )?(system|admin|developer)/i,
  /\b(system|developer)\s*(prompt|message)\b/i,
  /grant .*(permission|access|admin)/i,
  /\b(approve|reject|authorise|authorize|send|transfer|pay)\b.*\b(payment|money|funds|invoice)\b/i,
  /disable .*(sca|approval|security|two.factor)/i,
  /show .*(other|another) (company|customer|tenant)/i,
  /<script|<\/script|javascript:|onerror=|import\s*\(/i,
];

export type PromptAssessment = {
  readonly injectionSuspected: boolean;
  readonly refusedIntents: readonly string[];
  /** The goal text, truncated and stripped of control characters. */
  readonly sanitisedGoal: string;
};

export function assessPrompt(rawGoal: string): PromptAssessment {
  const sanitisedGoal = rawGoal
    // eslint-disable-next-line no-control-regex
    .replace(/[ -]/g, ' ')
    .trim()
    .slice(0, 2000);

  const refused: string[] = [];
  for (const pattern of INJECTION_PATTERNS) {
    if (pattern.test(sanitisedGoal)) refused.push(pattern.source);
  }

  return {
    injectionSuspected: refused.length > 0,
    refusedIntents: refused,
    sanitisedGoal,
  };
}

/* ------------------------------------------------------------- retrieval -- */

export type WidgetCandidate = {
  readonly id: MobileWidgetId;
  readonly title: string;
  readonly description: string;
  readonly removable: boolean;
  readonly pinned: boolean;
};

/**
 * The only widget metadata the generator may see.
 *
 * Deliberately excludes balances, transactions, names, identifiers and every
 * other piece of customer data — data minimisation per the v5.2 safety
 * contract.
 */
export function retrieveEntitledWidgets(ctx: EntitlementContext): readonly WidgetCandidate[] {
  const entitled = new Set(entitledWidgetIds(ctx));
  return MOBILE_WIDGETS.filter((w) => entitled.has(w.id)).map((w) => ({
    id: w.id,
    title: w.title,
    description: w.description,
    removable: w.removable,
    pinned: w.pinnedIndex !== undefined,
  }));
}

export function buildComposerContext(ctx: EntitlementContext): AIComposerContext {
  return {
    pageId: 'mobile-home',
    surface: 'SME Mobile HTML',
    roleIds: [ctx.user.roleId],
    allowedWidgetIds: [...entitledWidgetIds(ctx)],
    enabledFeatureFlags: Object.entries(ctx.featureFlags)
      .filter(([, v]) => v === true)
      .map(([k]) => k),
    redactedContextFields: ['balances', 'transactions', 'beneficiaries', 'cardNumbers'],
    excludedSensitiveFields: ['pan', 'cvv', 'pin', 'iban', 'accountNumber', 'email', 'permissions'],
    policyId: 'mobile-home-policy',
    policyVersion: 1,
  };
}

/* ---------------------------------------------------------- intent model -- */

type Goal = 'approvals-first' | 'spending-focus' | 'simplify' | 'cards-first' | 'unknown';

const GOAL_MATCHERS: readonly { goal: Goal; patterns: readonly RegExp[] }[] = [
  { goal: 'approvals-first', patterns: [/approv/i, /sign.?off/i, /waiting on me/i] },
  { goal: 'spending-focus', patterns: [/spend/i, /outgoing/i, /cash ?flow/i, /where.*money/i] },
  { goal: 'simplify', patterns: [/simpl/i, /declutter/i, /less|fewer|minimal/i, /clean/i] },
  { goal: 'cards-first', patterns: [/card/i, /expense/i] },
];

function classify(goal: string): Goal {
  for (const m of GOAL_MATCHERS) {
    if (m.patterns.some((p) => p.test(goal))) return m.goal;
  }
  return 'unknown';
}

const CLARIFYING_QUESTIONS: Readonly<Record<Goal, readonly string[]>> = {
  'approvals-first': [],
  'spending-focus': [],
  simplify: ['Would you like to keep recent activity, or reduce the page to the essentials only?'],
  'cards-first': [],
  unknown: [
    'What do you mostly use this app for — approving payments, watching spending, or checking cards?',
    'Should the home page be as short as possible, or is more detail useful?',
  ],
};

/* ------------------------------------------------------------- generator -- */

function operationsFor(
  goal: Goal,
  currentOrder: readonly MobileWidgetId[],
  candidates: readonly WidgetCandidate[],
): LayoutPatchOperation[] {
  const has = (id: MobileWidgetId) => currentOrder.includes(id);
  const available = (id: MobileWidgetId) => candidates.some((c) => c.id === id);
  const ops: LayoutPatchOperation[] = [];
  let n = 0;
  const op = (partial: Omit<LayoutPatchOperation, 'operationId'>): void => {
    n += 1;
    ops.push({ operationId: `op_${n}`, ...partial });
  };

  // Positions are relative to the movable region: the first three slots are
  // pinned, so a proposal never tries to occupy them.
  const FIRST_FREE = 3;

  switch (goal) {
    case 'approvals-first':
      // Needs-attention is already pinned near the top, so the useful change is
      // trimming what competes with it.
      if (has('money-flow')) op({ type: 'remove', widgetId: 'money-flow' });
      if (has('finance-shortcuts')) op({ type: 'remove', widgetId: 'finance-shortcuts' });
      if (has('recent-activity')) {
        op({ type: 'move', widgetId: 'recent-activity', breakpoint: 'narrow', position: { x: 0, y: FIRST_FREE, w: 4, h: 1 } });
      }
      break;

    case 'spending-focus':
      if (!has('money-flow') && available('money-flow')) op({ type: 'add', widgetId: 'money-flow' });
      op({ type: 'move', widgetId: 'money-flow', breakpoint: 'narrow', position: { x: 0, y: FIRST_FREE, w: 4, h: 1 } });
      if (has('recent-activity')) {
        op({ type: 'move', widgetId: 'recent-activity', breakpoint: 'narrow', position: { x: 0, y: FIRST_FREE + 1, w: 4, h: 1 } });
      }
      if (has('cards-summary')) op({ type: 'remove', widgetId: 'cards-summary' });
      break;

    case 'simplify':
      for (const id of ['money-flow', 'finance-shortcuts', 'cards-summary'] as const) {
        if (has(id)) op({ type: 'remove', widgetId: id });
      }
      break;

    case 'cards-first':
      if (!has('cards-summary') && available('cards-summary')) {
        op({ type: 'add', widgetId: 'cards-summary' });
      }
      op({ type: 'move', widgetId: 'cards-summary', breakpoint: 'narrow', position: { x: 0, y: FIRST_FREE, w: 4, h: 1 } });
      if (has('money-flow')) op({ type: 'remove', widgetId: 'money-flow' });
      break;

    case 'unknown':
    default:
      break;
  }
  return ops;
}

function rationaleFor(goal: Goal): readonly string[] {
  switch (goal) {
    case 'approvals-first':
      return [
        'Kept "Needs attention" high, because that is where approvals appear.',
        'Removed the finance panels that were competing with it for space.',
      ];
    case 'spending-focus':
      return [
        'Put "Money in and out" directly below the pinned area so the totals are the first thing you read.',
        'Kept recent activity next to it so a number can be traced to a movement.',
      ];
    case 'simplify':
      return ['Removed the optional panels and left the balance, actions and what needs attention.'];
    case 'cards-first':
      return ['Raised cards to the top of the movable area and dropped the spending summary.'];
    default:
      return [];
  }
}

function uncertaintyFor(goal: Goal, assessment: PromptAssessment): readonly string[] {
  const notes: string[] = [];
  if (goal === 'unknown') notes.push('I could not tell what you want from that description.');
  if (goal === 'simplify') notes.push('I have guessed which panels are optional for you.');
  if (assessment.injectionSuspected) {
    notes.push(
      'Part of your message looked like an instruction to change permissions or move money. I ignored that: I can only rearrange your home page.',
    );
  }
  notes.push('I cannot see your balances or transactions, only which panels you are entitled to.');
  return notes;
}

/* -------------------------------------------------------------- proposal -- */

export type ComposerResult = {
  readonly proposal: AIInterfaceProposal;
  /** Recomputed by the deterministic engine, never taken from the generator. */
  readonly policy: ReturnType<typeof validateOperations>;
  readonly assessment: PromptAssessment;
  readonly schemaValid: boolean;
  readonly schemaErrors: readonly string[];
};

export function proposeLayout(
  ctx: EntitlementContext,
  currentOrder: readonly MobileWidgetId[],
  rawGoal: string,
): ComposerResult {
  const assessment = assessPrompt(rawGoal);
  const candidates = retrieveEntitledWidgets(ctx);
  const goal = classify(assessment.sanitisedGoal);
  const questions = CLARIFYING_QUESTIONS[goal];

  proposalSeq += 1;
  const operations = questions.length > 0 ? [] : operationsFor(goal, currentOrder, candidates);

  // The generator's own view of validity is deliberately optimistic here so the
  // test suite can prove that it is discarded and recomputed below.
  const draft: AIInterfaceProposal = {
    proposalId: `prop_${String(proposalSeq).padStart(4, '0')}`,
    pageId: 'mobile-home',
    userGoal: assessment.sanitisedGoal,
    clarifyingQuestions: [...questions],
    rationale: [...rationaleFor(goal)],
    uncertainty: [...uncertaintyFor(goal, assessment)],
    operations,
    policyResult: { valid: true, violations: [] },
    status: questions.length > 0 ? 'needs-clarification' : 'ready',
  };

  // 1. Schema gate: the output must conform to the package's own schema.
  const validate = schemaValidator();
  const schemaValid = validate(draft);
  const schemaErrors = (validate.errors ?? []).map(
    (e) => `${e.instancePath || '/'} ${e.message ?? 'is invalid'}`,
  );

  // 2. Deterministic policy gate: recompute, never trust the model.
  const policy = schemaValid
    ? validateOperations(ctx, currentOrder, operations)
    : {
        valid: false,
        violations: ['The proposal did not match the required format.'],
        warnings: [],
        evaluatedPolicyVersion: POLICY_VERSION,
      };

  // A clarification-only proposal carries no operations, so a policy verdict
  // about the *current* layout must not turn it into a rejection.
  const needsClarification = draft.clarifyingQuestions.length > 0;
  const rejected = !needsClarification && (!schemaValid || !policy.valid);

  const proposal: AIInterfaceProposal = {
    ...draft,
    policyResult: { valid: policy.valid, violations: [...policy.violations] },
    status: rejected ? 'rejected' : draft.status,
  };

  return { proposal, policy, assessment, schemaValid, schemaErrors };
}

/** Widgets a user could still add, for the manual catalogue. */
export function addableWidgets(
  ctx: EntitlementContext,
  currentOrder: readonly MobileWidgetId[],
): readonly WidgetCandidate[] {
  return retrieveEntitledWidgets(ctx).filter((c) => !currentOrder.includes(c.id));
}

export { widgetById };
